class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Refs
    this.input = this.querySelector('input[type="search"]');
    this.resultsRoot = this.querySelector('#predictive-search');
    this.statusEl = document.getElementById('ps-status');
    this.form = this.querySelector('form');

    // State
    this.activeIndex = -1;
    this.optionEls = [];
    this.abortCtrl = null;
    this.lastTerm = '';
    this._minChars = 1;
    this._lsKey = 'ps-recent';

    // Typing
    this.input.addEventListener('input', this._debounce(() => this.onChange(), 220).bind(this));

    // Focus: show curated suggestions only when empty (idempotent)
    this.input.addEventListener('focus', () => {
      if (!this.input.value.trim()) this._showRecent();
    });

    // Keyboard nav
    this.input.addEventListener('keydown', (e) => {
      const key = e.key;
      if (key === 'Escape') { this.close(); return; }
      if (!this.optionEls.length || this.resultsRoot.hidden) return;

      if (key === 'ArrowDown' || key === 'Down') { e.preventDefault(); this._moveActive(1); }
      else if (key === 'ArrowUp' || key === 'Up') { e.preventDefault(); this._moveActive(-1); }
      else if (key === 'Home') { e.preventDefault(); this._setActive(0); }
      else if (key === 'End') { e.preventDefault(); this._setActive(this.optionEls.length - 1); }
      else if (key === 'Enter' && this.activeIndex > -1) {
        e.preventDefault();
        this._saveRecent(this.input.value.trim());
        this.optionEls[this.activeIndex].querySelector('a')?.click();
      }
    });

    // Clicks in results
    this.resultsRoot.addEventListener('click', (e) => {
      const chip = e.target.closest('.ps-chip[data-term]');
      if (chip) {
        this.input.value = chip.dataset.term;
        this.onChange();
        return;
      }
      if (e.target.closest('.ps-view-all')) {
        this._saveRecent(this.input.value.trim());
        return;
      }
      if (e.target.closest('.ps-link')) {
        this._saveRecent(this.input.value.trim());
        return;
      }
    });

    // Submit → save recent
    this.form?.addEventListener('submit', () => {
      this._saveRecent(this.input.value.trim());
    });

    // Observe panel open
    this._attachOpenObservers();
  }

  connectedCallback() {
    if (!this._openObserversAttached) this._attachOpenObservers();
  }

  /* ---------- Helpers ---------- */

  _isShowing(view) {
    return this.resultsRoot &&
           !this.resultsRoot.hidden &&
           this.resultsRoot.dataset.view === view &&
           this.resultsRoot.firstElementChild;
  }

  _attachOpenObservers() {
    if (this._openObserversAttached) return;
    this._openObserversAttached = true;

    try {
      const io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) this._onDrawerOpen();
      }, { root: null, threshold: 0.05 });
      io.observe(this);
      this._io = io;
    } catch(_) {
      // Fallback
      setTimeout(() => this._onDrawerOpen(), 0);
    }

    // Optional: custom event from your side panel
    document.addEventListener('side-panel:open', (e) => {
      if (e?.detail?.panel === 'search') this._onDrawerOpen();
    }, { passive: true });
  }

  _onDrawerOpen() {
    // If something is already visible, don’t re-render
    if (this.resultsRoot && !this.resultsRoot.hidden && this.resultsRoot.firstElementChild) return;

    const term = this.input?.value?.trim() || '';
    if (term.length >= this._minChars) {
      this.onChange();
    } else {
      this._showRecent();
    }
  }

  /* ---------- Predictive Search Flow ---------- */

  onChange() {
    const term = this.input.value.trim();
    if (term.length < this._minChars) { this._showRecent(); return; }
    if (term === this.lastTerm) return;
    this.lastTerm = term;
    this.getSearchResults(term);
  }

  async getSearchResults(term) {
    const url = `/search/suggest`
      + `?q=${encodeURIComponent(term)}`
      + `&resources[type]=product,collection,page,article`
      + `&resources[limit]=5`
      + `&resources[limit_scope]=each`
      + `&section_id=predictive-search`
      + `&options[prefix]=last`;

    // Cancel in-flight
    try { this.abortCtrl?.abort(); } catch(_) {}
    this.abortCtrl = new AbortController();

    // Skeleton + aria
    this._showSkeleton();
    this.resultsRoot.hidden = false;
    this.resultsRoot.setAttribute('aria-busy', 'true');
    this.input.setAttribute('aria-expanded', 'true');

    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortCtrl.signal
      });
      if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);

      const html = await res.text();
      const section = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!section) { this.close(); return; }

      this._swapAnimated(section.innerHTML);
      this._afterRender(term);
      this.open();
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(err);
        this.close();
      }
    } finally {
      this.resultsRoot.setAttribute('aria-busy', 'false');
    }
  }

  _afterRender(term) {
    this.optionEls = Array.from(this.resultsRoot.querySelectorAll('[role="option"].ps-item'));
    this.optionEls.forEach((li, i) => { if (!li.id) li.id = `ps-opt-${i}`; });
    this.activeIndex = -1;
    this.input.setAttribute('aria-activedescendant', '');

    this.optionEls.forEach((li, i) => {
      li.addEventListener('mouseenter', () => this._setActive(i));
    });

    this._highlight(term);

    const count = this.optionEls.length;
    if (this.statusEl) {
      this.statusEl.textContent = count
        ? `${count} result${count === 1 ? '' : 's'} for ${term}`
        : `No results for “${term}”`;
    }

    // Mark current view
    this.resultsRoot.dataset.view = 'results';
  }

  _highlight(term) {
    if (!term) return;
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(esc(term), 'ig');
    this.resultsRoot.querySelectorAll('.ps-title').forEach(el => {
      const txt = el.textContent || '';
      el.innerHTML = txt.replace(re, (m) => `<mark>${m}</mark>`);
    });
  }

  open() {
    this.resultsRoot.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.resultsRoot.firstElementChild || this.resultsRoot.hidden) {
      this.resultsRoot.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this._clear();
      return;
    }
    const current = this.resultsRoot.firstElementChild;
    current.classList.remove('ps-anim-enter');
    current.classList.add('ps-anim-leave');
    current.addEventListener('animationend', () => {
      this.resultsRoot.hidden = true;
      this._clear();
      this.input.setAttribute('aria-expanded', 'false');
    }, { once: true });
  }

  _clear() {
    this.resultsRoot.innerHTML = '';
    this.optionEls = [];
    this.activeIndex = -1;
    this.input.setAttribute('aria-activedescendant', '');
    this.resultsRoot.dataset.view = ''; // reset view
  }

  _setActive(i) {
    if (!this.optionEls.length) return;
    this.optionEls.forEach(li => li.setAttribute('aria-selected', 'false'));
    this.activeIndex = Math.max(0, Math.min(i, this.optionEls.length - 1));
    const current = this.optionEls[this.activeIndex];
    current.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', current.id || '');
    current.scrollIntoView({ block: 'nearest' });
  }

  _moveActive(step) {
    const next = this.activeIndex + step;
    if (next < 0) { this.activeIndex = -1; this.input.setAttribute('aria-activedescendant',''); return; }
    this._setActive(next);
  }

  _showSkeleton() {
    const rows = 5;
    const skeleton = `
      <div class="ps-anim-enter">
        <div class="ps-skeleton">
          ${Array.from({length: rows}).map(() => `
            <div class="ps-skel-row">
              <span class="ps-skel-thumb"></span>
              <span class="ps-skel-line"></span>
            </div>`).join('')}
        </div>
      </div>`;
    this.resultsRoot.innerHTML = skeleton;
    this.resultsRoot.hidden = false;
  }

  _showRecent() {
    // If suggestions already visible, just ensure open and bail (no re-animation)
    if (this._isShowing('recent')) { this.open(); return; }

    // Build recent chips (optional)
    let chipsHtml = '';
    try {
      const recent = JSON.parse(localStorage.getItem(this._lsKey) || '[]');
      if (recent.length) {
        chipsHtml = `<div class="ps-recent">
          ${recent.map((r) => `<button type="button" class="ps-chip" data-term="${this._esc(r)}">${this._esc(r)}</button>`).join('')}
        </div>`;
      }
    } catch(_) {}

    // Pull curated suggestions from the template
    const tpl = document.getElementById('ps-suggestions');
    const wrapper = document.createElement('div');

    // Animate only if panel is hidden or empty (first mount)
    const shouldAnimate = this.resultsRoot.hidden || !this.resultsRoot.firstElementChild;
    if (shouldAnimate) wrapper.classList.add('ps-anim-enter');

    wrapper.innerHTML = tpl ? tpl.innerHTML : `
      <div class="ps-empty-wrap">
        <p class="ps-empty">${this._t('general.search.start_typing') || 'Start typing to search'}</p>
      </div>
    `;

    // Append chips (if any)
    const container = wrapper.querySelector('.ps-empty-wrap') || wrapper;
    if (chipsHtml) container.insertAdjacentHTML('beforeend', chipsHtml);

    // Mount
    this.resultsRoot.innerHTML = '';
    this.resultsRoot.appendChild(wrapper);
    this.resultsRoot.dataset.view = 'recent'; // mark view
    this.open();

    // Reset keyboard nav targets
    this.optionEls = Array.from(this.resultsRoot.querySelectorAll('[role="option"].ps-item'));
    this.activeIndex = -1;
    this.input.setAttribute('aria-activedescendant', '');
    this.optionEls.forEach((li, i) => { li.addEventListener('mouseenter', () => this._setActive(i)); });
  }

  _swapAnimated(markup) {
    const prev = this.resultsRoot.firstElementChild;
    if (prev && prev.classList.contains('ps-anim-leave')) prev.classList.remove('ps-anim-leave');

    const wrapper = document.createElement('div');
    wrapper.className = 'ps-anim-enter';
    wrapper.innerHTML = markup;

    this.resultsRoot.innerHTML = '';
    this.resultsRoot.appendChild(wrapper);

    // reflow + keep a single, clean enter animation
    void wrapper.offsetWidth;
    wrapper.classList.remove('ps-anim-enter');
    void wrapper.offsetWidth;
    wrapper.classList.add('ps-anim-enter');
  }

  _debounce(fn, wait) { let t; return (...args)=>{ clearTimeout(t); t=setTimeout(()=>fn.apply(this,args), wait); }; }
  _esc(s){ return String(s).replace(/[&<>"']/g,(c)=>({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])); }
  _t(key, params){
    try{
      if (window.themeTranslations && key in window.themeTranslations){
        let str = window.themeTranslations[key];
        if (params) Object.keys(params).forEach(k => {
          str = str.replace(new RegExp(`{{\\s*${k}\\s*}}`,'g'), params[k]);
        });
        return str;
      }
    } catch(_){}
    return null;
  }

  _saveRecent(term){
    if (!term) return;
    try{
      const list = JSON.parse(localStorage.getItem(this._lsKey) || '[]');
      const next = [term, ...list.filter(t => t !== term)].slice(0, 6);
      localStorage.setItem(this._lsKey, JSON.stringify(next));
    }catch(_){}
  }
}

// Safe register
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
